'use client'

import { useCallback, useContext, useEffect, useMemo, useRef, useState, createContext, ReactNode } from "react";
import { BeforeSaveHandler, ClientObjectUpdate, ENTITY, OBJECT_TYPE } from "../eventsDispatcher/eventTypes";
import { DataBaseContext } from "./db.";
import { DispatcherContext } from "../eventsDispatcher";
import { format } from "date-fns";
import { ChecklistItem } from "../state/ChecklistItem";
import { Task } from "../state/Task";
import { TaskList } from "../state/TaskList";
import { formatMins, formatMinsSimple, parseMins, totalTime } from "../state/helpers";
import { Wheel } from "../state/Wheel";
import { Goal } from "../state/Goal";
import { LifePart } from "../state/LifePart";
import { DayPlan } from "../state/DayPlan";
import { useDebounce } from "@uidotdev/usehooks";
import { Countable } from "../state/common";
import { Tracker, TrackedValue } from "../state/Tracker";
import { Reflection } from "../state/Reflection";
import { WheelSnapshot } from "../state/WheelSnapshot";
import { Conclusion } from "../state/Conclusion";
import { Decision } from "../state/Decision";
import { RoleEfficiencySnapshot } from "../state/RoleEfficiencySnapshot";
import { MeSnapshot } from "../state/MeSnapshot";
import { TaskComment } from "../state/TaskComment";
import { Duty } from "../state/Duty";
import usePendingUpdates from "../pendindUpdates";

export type { TrackedValue };

export interface ObjectStorage<T extends ENTITY> {
    isLoading: boolean
    objects: Record<string, T>
    save: (uuid: string, update: Partial<T>) => void,
    create: (obj: T) => void,
    delete: (uuid: string) => void
}

export function useObjects<T extends ENTITY>(type: OBJECT_TYPE): ObjectStorage<T> {
    const [objects, setObjects] = useState<Record<string, T>>({});
    const [isLoading, setIsLoading] = useState(true);
    const db = useContext(DataBaseContext);
    const dispatcher = useContext(DispatcherContext);
    const listener = useRef(crypto.randomUUID());
    const [pendingUpdates, setPendingUpdates] = useState<Record<string, T>>({});
    const pending = useDebounce(pendingUpdates, 1000);
    const { deletePendingUpdatesForObject } = usePendingUpdates();

    const saveObject = useCallback((uuid: string, update: Partial<T>) => {

        // Обновляем локальный стейт сразу
        // console.log('saving', update, uuid);
        setObjects(prev => {
            // console.log('save object', update);
            const newObjects = { ...prev }
            const old = newObjects[uuid];
            newObjects[uuid] = { ...old, ...update };
            // console.log("new objects", newObjects);
            return newObjects
        });
        // Отправляем только изменённые поля
        setPendingUpdates(old => {
            const newUpdates = { ...old };
            const currentObject = objects[uuid] || {};
            const filteredUpdate: Partial<T> = {} as Partial<T>;

            // Фильтруем update, оставляем только изменённые поля
            for (const key of Object.keys(update) as Array<keyof T>) {
                if (update[key] !== currentObject[key]) {
                    (filteredUpdate as any)[key] = update[key];
                }
            }

            // Если есть изменения, обновляем pendingUpdates
            const keys = Object.keys(filteredUpdate);
            if (keys.length > 0) {
                const existingPending = old[uuid] || {};
                newUpdates[uuid] = { ...existingPending, ...filteredUpdate };
            }
            return newUpdates
        })
    }, [type, dispatcher, objects]);

    const createObject = useCallback((object: T) => {

        // Обновляем локальный стейт сразу
        setObjects(prev => {
            const newObjects = { ...prev }
            newObjects[object.uuid] = object;
            return newObjects
        });

        if (!dispatcher) return;
        dispatcher.sendLocalUpdate({
            data: object,
            object_type: type,
            uuid: object.uuid,
            type: 'OBJECT',
            toDelete: false,
            sourceWidget: listener.current
        });
    }, [type, dispatcher]);

    const deleteObject = useCallback(async (uuid: string) => {
        // Обновляем локальный стейт сразу
        setObjects(prev => {
            const newObjects = { ...prev };
            delete newObjects[uuid];
            return newObjects;
        });

        // Удаляем из pending updates в IndexedDB
        if (db) {
            await deletePendingUpdatesForObject(uuid, type);
        }

        if (!dispatcher) return;
        dispatcher.sendLocalUpdate({
            toDelete: true,
            object_type: type,
            uuid: uuid,
            type: 'OBJECT',
            data: {},
            sourceWidget: listener.current
        });
    }, [type, dispatcher, db, deletePendingUpdatesForObject]);

    useEffect(() => {
        (async () => {
            const allObjects = await new Promise<T[]>((resolve, reject) => {
                if (!db) {
                    reject(new Error('db not initiated'));
                    return;
                }
                const transaction = db.db.transaction([type], 'readonly');
                const store = transaction.objectStore(type);
                const request = store.getAll();
                request.onsuccess = () => resolve(request.result || []);
                request.onerror = () => reject(request.error);
            });
            let result: Record<string, T> = {}
            allObjects.map(obj => result[obj.uuid] = obj)
            setObjects(result);
            setIsLoading(false);
        })()
    }, [type, db?.db]);

    useEffect(() => {
        if (!dispatcher)
            return;
        // console.log(`register table ${type} listener`);
        const listenerUUID = dispatcher.addTableUpdatesListener((update) => {
            // console.log(`table ${type} listener get update`, update);
            if (update.toDelete) {
                setObjects(prev => {
                    const newObjects = { ...prev };
                    delete newObjects[update.uuid];
                    return newObjects;
                });
            } else {
                setObjects(prev => {
                    const newObjects = { ...prev };
                    const oldObject = prev[update.uuid] || { uuid: update.uuid };
                    const objToStore = { ...oldObject, ...update.data };
                    // console.log(`store object in memory ${type}`, objToStore);
                    newObjects[update.uuid] = objToStore;
                    return newObjects;
                });
            }
        }, type);
        return () => dispatcher.removeTableUpdatesListener(listenerUUID);
    }, [type, dispatcher?.addTableUpdatesListener]);

    useEffect(() => {
        if (!dispatcher) return;
        const keys = Object.keys(pending);
        if (keys.length > 0) {
            keys.map(key => {
                dispatcher.sendLocalUpdate({
                    data: pending[key],
                    object_type: type,
                    uuid: key,
                    type: 'OBJECT',
                    toDelete: false,
                    sourceWidget: listener.current
                });
            });
            setPendingUpdates({});
        }

    }, [pending])

    const final = useMemo(() => {
        return {
            isLoading,
            objects,
            save: saveObject,
            create: createObject,
            delete: deleteObject
        }
    }, [objects, createObject, deleteObject, saveObject, isLoading])

    return final;
}

export interface TaskStats {
    tasks: number;
    finishedTasks: number;
    notReadyTasks: number;
    duration: { asString: string; minutes: number };
    todayFinishedTasks: number;
    beforeFinishedTasks: number;
    needMoney: number;
    progress?: number;
}


export function useTaskStats(task: Task | null, checklistItems: ChecklistItem[], date?: Date): TaskStats {
    if (!task) {
        return {
            tasks: 0,
            finishedTasks: 0,
            notReadyTasks: 0,
            duration: { asString: '', minutes: 0 },
            todayFinishedTasks: 0,
            beforeFinishedTasks: 0,
            needMoney: 0,
            progress: undefined
        };
    }


    const items = checklistItems.filter((item): item is ChecklistItem => item !== undefined);
    const finishedTasks = items.filter(t => t.isDone);
    const notReadyTasks = items.filter(t => !t.isDone);

    const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
    const todayFinishedTasks = date
        ? finishedTasks.filter(t => t.dateDone && t.dateDone === dateStr).length
        : 0;
    const beforeFinishedTasks = date
        ? finishedTasks.filter(t => !t.dateDone || t.dateDone === '' || t.dateDone < dateStr).length
        : 0;

    const totalMins = notReadyTasks.reduce((sum, item) => {
        return sum + parseMins(item.needTime || "00:00");
    }, 0);

    const needMoney = notReadyTasks.reduce((sum, item) => {
        return sum + (item.needMoney || 0);
    }, 0);

    return {
        tasks: items.length,
        finishedTasks: finishedTasks.length,
        notReadyTasks: notReadyTasks.length,
        duration: {
            minutes: totalMins,
            asString: formatMins(totalMins)
        },
        todayFinishedTasks,
        beforeFinishedTasks,
        needMoney,
        progress: items.length > 0 ? Math.floor(finishedTasks.length * 100 / items.length) : undefined
    };
}

export function useTaskChecklistItems(taskUuid?: string): ChecklistItem[] {
    const { checklistItems } = useData();
    const items = useMemo(() => {
        return Object.values(checklistItems.objects).filter(i => i.task === taskUuid).sort((a, b) => (a.order || 0) - (b.order || 0))
    }, [taskUuid, checklistItems.objects])
    return items
}

export function useTaskComments(taskUuid?: string): TaskComment[] {
    const db = useContext(DataBaseContext);
    const dispatcher = useContext(DispatcherContext);
    const [comments, setComments] = useState<TaskComment[]>([]);
    const commentsRef = useRef<TaskComment[]>([]);
    commentsRef.current = comments;

    const sortComments = (items: TaskComment[]) =>
        [...items].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

    // Комментарии не держим в общем хранилище и не читаем всю таблицу:
    // грузим из IndexedDB только записи текущей задачи по индексу 'task'
    // (тот же подход, что и для точек графиков в useTrackedValues)
    useEffect(() => {
        if (!taskUuid || !db) {
            setComments([]);
            return;
        }
        let cancelled = false;
        const transaction = db.db.transaction(['TASK_COMMENT'], 'readonly');
        const store = transaction.objectStore('TASK_COMMENT');
        const index = store.index('task');
        const request = index.getAll(taskUuid);
        request.onsuccess = () => {
            if (cancelled) return;
            setComments(sortComments((request.result || []) as TaskComment[]));
        };
        request.onerror = () => {
            if (!cancelled) setComments([]);
        };
        return () => { cancelled = true; };
    }, [taskUuid, db?.db]);

    // Подписка на изменения: свои правки и апдейты от других клиентов.
    // Применяем событие к локальному списку (в событиях приходят полные данные объекта),
    // чтобы не гонять повторное чтение из БД и не зависеть от порядка записи
    useEffect(() => {
        if (!dispatcher || !taskUuid) return;
        const listenerId = dispatcher.addTableUpdatesListener((update) => {
            if (update.toDelete) {
                setComments(prev => prev.filter(c => c.uuid !== update.uuid));
                return;
            }
            const incoming = update.data as Partial<TaskComment> | undefined;
            const existing = commentsRef.current.find(c => c.uuid === update.uuid);
            if (existing) {
                setComments(prev => sortComments(prev.map(c => c.uuid === update.uuid ? { ...c, ...incoming } : c)));
            } else if (incoming?.task === taskUuid) {
                setComments(prev => sortComments([...prev, { ...incoming, uuid: update.uuid } as TaskComment]));
            }
        }, 'TASK_COMMENT');
        return () => dispatcher.removeTableUpdatesListener(listenerId);
    }, [dispatcher, taskUuid]);

    return comments;
}

export function useTaskCommentOperations() {
    const dispatcher = useContext(DispatcherContext);
    const listener = useRef(crypto.randomUUID());

    const send = useCallback((uuid: string, data: Partial<TaskComment>, toDelete = false) => {
        if (!dispatcher) return;
        dispatcher.sendLocalUpdate({
            type: 'OBJECT',
            object_type: 'TASK_COMMENT',
            uuid,
            data: toDelete ? {} : data,
            toDelete,
            sourceWidget: listener.current,
        });
    }, [dispatcher]);

    return useMemo(() => ({
        create: (comment: TaskComment) => send(comment.uuid, comment),
        save: (uuid: string, data: Partial<TaskComment>) => send(uuid, data),
        delete: (uuid: string) => send(uuid, {}, true),
    }), [send]);
}

export function useTrackedValues(trackerUuid?: string, refreshKey: number = 0): TrackedValue[] {
    const db = useContext(DataBaseContext);
    const dispatcher = useContext(DispatcherContext);
    const [values, setValues] = useState<TrackedValue[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const reloadValues = useCallback(async () => {
        if (!trackerUuid || !db) {
            setValues([]);
            setIsLoading(false);
            return;
        }

        try {
            const allValues = await new Promise<TrackedValue[]>((resolve, reject) => {
                const transaction = db.db.transaction(['TRACKED_VALUE'], 'readonly');
                const store = transaction.objectStore('TRACKED_VALUE');
                const index = store.index('tracker');
                const request = index.getAll(trackerUuid);
                request.onsuccess = () => resolve(request.result || []);
                request.onerror = () => reject(request.error);
            });
            setValues(allValues.sort((a, b) => b.timestamp - a.timestamp));
        } catch (error) {
            console.error('Error loading tracked values:', error);
            setValues([]);
        } finally {
            setIsLoading(false);
        }
    }, [trackerUuid, db]);

    useEffect(() => {
        reloadValues();
    }, [trackerUuid, db, refreshKey, reloadValues]);

    // Подписка на обновления от других клиентов
    useEffect(() => {
        if (!dispatcher) return;

        const handleUpdate = (update: any) => {
            if (update.object_type === 'TRACKED_VALUE') {
                reloadValues();
            }
        };

        const listenerId = dispatcher.addTableUpdatesListener(handleUpdate, 'TRACKED_VALUE');
        return () => dispatcher.removeTableUpdatesListener(listenerId);
    }, [dispatcher, reloadValues]);

    return values;
}

export function useTrackedValueOperations() {
    const db = useContext(DataBaseContext);
    const dispatcher = useContext(DispatcherContext);
    const listener = useRef(crypto.randomUUID());

    const saveValue = useCallback(async (value: TrackedValue): Promise<void> => {
        if (!db) throw new Error('db not initiated');

        // Сохраняем в IndexedDB
        await new Promise((resolve, reject) => {
            const transaction = db.db.transaction(['TRACKED_VALUE'], 'readwrite');
            const store = transaction.objectStore('TRACKED_VALUE');
            const request = store.put(value);
            request.onsuccess = () => resolve(value);
            request.onerror = () => reject(request.error);
        });

        // Отправляем обновление через WebSocket
        if (dispatcher) {
            dispatcher.sendLocalUpdate({
                type: 'OBJECT',
                object_type: 'TRACKED_VALUE',
                uuid: value.uuid,
                data: value,
                toDelete: false,
                sourceWidget: listener.current
            });
        }
    }, [db, dispatcher]);

    const deleteValue = useCallback(async (uuid: string): Promise<void> => {
        if (!db) throw new Error('db not initiated');

        // Удаляем из IndexedDB
        await new Promise((resolve, reject) => {
            const transaction = db.db.transaction(['TRACKED_VALUE'], 'readwrite');
            const store = transaction.objectStore('TRACKED_VALUE');
            const request = store.delete(uuid);
            request.onsuccess = () => resolve(uuid);
            request.onerror = () => reject(request.error);
        });

        // Отправляем обновление через WebSocket
        if (dispatcher) {
            dispatcher.sendLocalUpdate({
                type: 'OBJECT',
                object_type: 'TRACKED_VALUE',
                uuid: uuid,
                data: {},
                toDelete: true,
                sourceWidget: listener.current
            });
        }
    }, [db, dispatcher]);

    return { saveValue, deleteValue };
}


export interface DataContextValue {
    wheels: ObjectStorage<Wheel>;
    goals: ObjectStorage<Goal>;
    roles: ObjectStorage<LifePart>;
    tasks: ObjectStorage<Task>;
    plans: ObjectStorage<DayPlan>;
    checklistItems: ObjectStorage<ChecklistItem>;
    trackers: ObjectStorage<Tracker>;
    taskLists: ObjectStorage<TaskList>;
    reflections: ObjectStorage<Reflection>;
    wheelSnapshots: ObjectStorage<WheelSnapshot>;
    meSnapshots: ObjectStorage<MeSnapshot>;
    roleEfficiencySnapshots: ObjectStorage<RoleEfficiencySnapshot>;
    conclusions: ObjectStorage<Conclusion>;
    decisions: ObjectStorage<Decision>;
    duties: ObjectStorage<Duty>;
}

export const DataContext = createContext<DataContextValue | null>(null);

export function useData(): DataContextValue {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error("useData must be used within a DataProvider");
    }
    return context;
}

/**
 * Хук для получения текущих счётчиков объектов для проверки лимитов
 */
export function useLimitCounts() {
    const { roles, tasks, checklistItems, trackers, taskLists } = useData();

    return useMemo(() => ({
        lifeParts: Object.keys(roles.objects).length,
        tasks: Object.keys(tasks.objects).length,
        checklists: Object.keys(checklistItems.objects).length,
        trackers: Object.keys(trackers.objects).length,
        taskLists: Object.keys(taskLists.objects).length,
    }), [roles.objects, tasks.objects, checklistItems.objects, trackers.objects, taskLists.objects]);
}

export interface GarbageCollectReport {
    trackersDeleted: number;
    tasksDeleted: number;
    dayPlansDeleted: number;
    checklistItemsDeleted: number;
}

export function useGarbageCollect() {
    const { roles, tasks, checklistItems, trackers, plans } = useData();

    const execute = (): GarbageCollectReport => {
        const report: GarbageCollectReport = {
            trackersDeleted: 0,
            tasksDeleted: 0,
            dayPlansDeleted: 0,
            checklistItemsDeleted: 0,
        };

        const lifePartIds = Object.keys(roles.objects);

        // 1. Удаляем трекеры, которые указывают на несуществующую lifepart
        const trackersToDelete = Object.entries(trackers.objects)
            .filter(([, tracker]) => !lifePartIds.includes(tracker.lifepart))
            .map(([uuid]) => uuid);

        trackersToDelete.forEach(uuid => {
            trackers.delete(uuid);
            report.trackersDeleted++;
        });

        // 2. Удаляем задачи, которые завершены и у которых пустой контекст
        const tasksToDelete = Object.entries(tasks.objects)
            .filter(([, task]) => task.isDone && (!task.context || task.context.trim() === ''))
            .map(([uuid]) => uuid);

        tasksToDelete.forEach(uuid => {
            tasks.delete(uuid);
            report.tasksDeleted++;
        });

        // Пересчитываем ID задач после удаления
        const taskIds = Object.keys(tasks.objects);

        const fourWeeksAgo = new Date();
        fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

        // 3. Удаляем dayPlan, которые завершены и старше 4 недель
        // 4. Удаляем dayPlan, которые указывают на несуществующий task
        const dayPlansToDelete = Object.entries(plans.objects)
            .filter(([, plan]) => {
                const planDate = plan.createdAt ? new Date(plan.createdAt) : new Date(plan.date);
                const isOldAndDone = plan.isDone && planDate < fourWeeksAgo;
                const taskMissing = !taskIds.includes(plan.task);
                return isOldAndDone || taskMissing;
            })
            .map(([uuid]) => uuid);

        dayPlansToDelete.forEach(uuid => {
            plans.delete(uuid);
            report.dayPlansDeleted++;
        });

        // 5. Удаляем элементы чеклистов, которые указывают на несуществующий task
        const checklistItemsToDelete = Object.entries(checklistItems.objects)
            .filter(([, item]) => !taskIds.includes(item.task))
            .map(([uuid]) => uuid);

        checklistItemsToDelete.forEach(uuid => {
            checklistItems.delete(uuid);
            report.checklistItemsDeleted++;
        });

        return report;
    };

    return { execute };
}

interface DataProviderProps {
    children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
    const dispatcher = useContext(DispatcherContext);
    // Wheel с uuid = "wheel"
    const wheels = useObjects<Wheel>("WHEEL");

    // Goals - все цели
    const goals = useObjects<Goal>("GOAL");

    // Roles (LifeParts) - все части
    const roles = useObjects<LifePart>("LIFEPART");
    const tasks = useObjects<Task>("TASK");
    const plans = useObjects<DayPlan>("PLAN");
    const checklistItems = useObjects<ChecklistItem>("CHECKLISTITEM");
    const trackers = useObjects<Tracker>("TRACKER");
    const taskLists = useObjects<TaskList>("TASKLIST");
    const reflections = useObjects<Reflection>("REFLECTION");
    const wheelSnapshots = useObjects<WheelSnapshot>("WHEEL_SNAPSHOT");
    const meSnapshots = useObjects<MeSnapshot>("ME_SNAPSHOT");
    const roleEfficiencySnapshots = useObjects<RoleEfficiencySnapshot>("ROLE_EFFICIENCY_SNAPSHOT");
    const conclusions = useObjects<Conclusion>("CONCLUSION");
    const decisions = useObjects<Decision>("DECISION");
    const duties = useObjects<Duty>("DUTY");

    const value: DataContextValue = {
        wheels,
        goals,
        roles,
        tasks,
        plans,
        checklistItems,
        trackers,
        taskLists,
        reflections,
        wheelSnapshots,
        meSnapshots,
        roleEfficiencySnapshots,
        conclusions,
        decisions,
        duties,
    }

    // BeforeSave middleware для задач - устанавливает completedAt при изменении isDone
    const beforeTaskSave = useCallback((update: ClientObjectUpdate): ClientObjectUpdate => {
        if (update.toDelete) return update;
        
        const taskUpdate: Partial<Task> = update.data;
        
        // Устанавливаем completedAt при изменении isDone
        if (taskUpdate.isDone !== undefined) {
            const processedUpdate: ClientObjectUpdate = {
                ...update,
                data: {
                    ...taskUpdate,
                    completedAt: taskUpdate.isDone ? Date.now() : undefined,
                }
            };
            return processedUpdate;
        }
        
        return update;
    }, []);

    const afterTaskSave = useCallback((event: ClientObjectUpdate) => {
        console.log('tasks after save');
        if (!!event.toDelete) {
            return
        }
        const update: Partial<Task> = event.data;
        const task = tasks.objects[event.uuid];
        
        // Обновляем связанный checklist item при изменении isDone или description
        if ((update.isDone !== undefined) || (update.description !== undefined)) {
            if (!task || !task.parentChecklistItem) {
                return
            }
            const cl = checklistItems.objects[task.parentChecklistItem];
            if (!cl) return;
            if (cl.isDone !== task.isDone || (cl.title !== task.description)) {
                checklistItems.save(cl.uuid, { title: task.description, isDone: task.isDone, dateDone: task.isDone ? format(new Date(), "yyyy-MM-dd") : undefined })
            }
        }
    }, [checklistItems, tasks]);

    const afterClSave = useCallback((event: ClientObjectUpdate) => {
        // console.log('cl after save');
        const update: Partial<ChecklistItem> = event.data;
        if ((update.isDone === undefined) && (update.needTime === undefined)) return;

        let cl = { ...checklistItems.objects[event.uuid], ...update };
        let task = tasks.objects[cl.task];
        // console.log('cl task', task);
        if (!task) return;
        while (!!task.parentChecklistItem) {
            let needTime = totalTime(Object.values(checklistItems.objects).filter(cl => cl.task === task.uuid && !cl.isDone) as Countable[]);
            let parentCl = checklistItems.objects[task.parentChecklistItem];
            if (!parentCl) {
                return
            }
            let newNeedTime = formatMinsSimple(needTime.minutes);
            if (parentCl.needTime === newNeedTime) return

            checklistItems.save(parentCl.uuid, { needTime: newNeedTime })
            if (!parentCl.innerTask) return
            cl = parentCl
            task = tasks.objects[cl.task]

        }


    }, [checklistItems, tasks]);

    useEffect(() => {
        if (dispatcher) {
            const beforeTask = dispatcher.addBeforeSaveListener(beforeTaskSave, "TASK");
            const tasks = dispatcher.addTableUpdatesListener(afterTaskSave, "TASK");
            const cls = dispatcher.addTableUpdatesListener(afterClSave, "CHECKLISTITEM");
            return () => {
                dispatcher.removeBeforeSaveListener(beforeTask);
                dispatcher.removeTableUpdatesListener(tasks);
                dispatcher.removeTableUpdatesListener(cls);
            }
        }
    }, [dispatcher, beforeTaskSave, afterTaskSave, afterClSave])

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
}

